import type { OrderDetails } from '@/entities/order/model'
import { useMemo } from 'react'
import { useRawOrdersQuery } from '@/entities/order/model'
import { useRealtimeStore } from '@/features/realtime/model'

/**
 * Hook to get a specific order details from the query cache
 * and subscribe to updates
 *
 * @param orderId - The ID of the order to retrieve
 * @returns An object containing the order details and loading state
 */
export function useOrderDetails(orderId: string): {
  order: OrderDetails | undefined
  isLoading: boolean
} {
  // Use the raw orders query which properly handles real-time updates and cache invalidation
  const { data: orders, isLoading: queryLoading } = useRawOrdersQuery()

  // Get real-time orders as fallback
  const realtimeOrders = useRealtimeStore(state => state.realtimeOrders)
  const useRealtimeData = useRealtimeStore(state => state.useRealtime)

  const order = useMemo(() => {
    // First try to find in the main orders data (which includes real-time updates)
    const foundOrder = orders?.find(o => o.orderId === orderId)

    // If not found and we have real-time data, check there as well
    if (!foundOrder && useRealtimeData) {
      return realtimeOrders.find(o => o.orderId === orderId)
    }

    return foundOrder
  }, [orders, orderId, realtimeOrders, useRealtimeData])

  const isLoading = queryLoading && !order

  return { order, isLoading }
}
